import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Icon } from '../../Atoms/Icon';
import { DateRange } from '../DateRange';
import { StyledPopover, StyledInputCalendar, StyledRawInput } from './index.styles';
import { defaultErrorMessage, normalizeDatesByMode } from './index.constants';

export const InputDateRange = ({
    maxDaysInterval,
    initialDate: initialDateProp,
    finalDate: finalDateProp,
    label,
    fullWidth,
    onChange,
    onError,
    ref,
    inputRef: inputRefProp,
    message,
    responsiveBreakpoint,
    mode,
    ...otherProps
}) => {
    const theme = useTheme();
    const { disabled } = otherProps;

    const [value, setValue] = useState('');
    const [dateValues, setDateValues] = useState({});
    const [open, setOpen] = useState(false);
    const [maxDays, setMaxDays] = useState(maxDaysInterval);
    const [currentMode, setCurrentMode] = useState(mode);
    const [error, setError] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [width, setWidth] = React.useState(window.innerWidth);
    const inputRef = inputRefProp || useRef();

    const { initialDate: initial, finalDate: final } = dateValues;

    const verifyInterval = (initialDate, finalDate) => {
        const intervalInTime = finalDate.getTime() - initialDate.getTime();
        const intervalInDays = intervalInTime / (1000 * 3600 * 24);
        return intervalInDays < maxDays;
    };

    const onCalendarChange = (initialDate, finalDate, forceMode = '') => {
        setValue(normalizeDatesByMode(initialDate, finalDate, forceMode || currentMode));
        setDateValues({ initialDate, finalDate });
        setError(false);
        setMessageError('');
        if (initialDate && finalDate) {
            const datesAreGood = verifyInterval(initialDate, finalDate);
            if (datesAreGood) {
                onChange(initialDate, finalDate);
                return;
            }
            setError(true);
            setMessageError(message || defaultErrorMessage(maxDays));
            onError();
        }
    };

    const handleResize = () => setWidth(window.innerWidth);

    useEffect(() => {
        setValue(normalizeDatesByMode(initialDateProp, finalDateProp, currentMode));
        setDateValues({
            initialDate: initialDateProp && new Date(initialDateProp),
            finalDate: finalDateProp && new Date(finalDateProp),
        });
        const hasError =
            initialDateProp &&
            finalDateProp &&
            !verifyInterval(new Date(initialDateProp), new Date(finalDateProp));
        setError(hasError);
        setMessageError(hasError ? message || defaultErrorMessage(maxDays) : '');
    }, [initialDateProp, finalDateProp]);

    useEffect(() => {
        setIsMobile(width < responsiveBreakpoint);
    }, [width]);

    useEffect(() => {
        setMaxDays(mode === 'days' ? maxDaysInterval : Number.MAX_SAFE_INTEGER);
        if (currentMode !== mode) {
            onCalendarChange(null, null, mode);
            setOpen(false);
            setCurrentMode(mode);
        }
    }, [mode]);

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        return () => window.removeEventListener('resize', handleResize, false);
    }, []);

    return (
        <StyledInputCalendar fullWidth={fullWidth} ref={ref}>
            <StyledRawInput
                inputRef={inputRef}
                value={value}
                fullWidth={fullWidth}
                label={label}
                onClick={() => setOpen(!open)}
                error={error}
                message={messageError}
                icon={
                    <Icon
                        icon={['far', 'calendar-pen']}
                        id="input-caledar-icon"
                        size="lg"
                        onClick={() => !disabled && setOpen(!open)}
                        color={error ? theme.global.colors.error : theme.global.colors.primary}
                    />
                }
                inputMode="none"
                {...otherProps}
            />
            <StyledPopover
                bindTo="input-caledar-icon"
                open={open}
                onClose={() => setOpen(false)}
                showCloseIcon
            >
                <DateRange
                    initialSelected={initial}
                    finalSelected={final}
                    onChange={onCalendarChange}
                    isMobile={isMobile}
                    mode={currentMode}
                />
            </StyledPopover>
        </StyledInputCalendar>
    );
};

InputDateRange.propTypes = {
    maxDaysInterval: PropTypes.number,
    initialDate: PropTypes.instanceOf(Date),
    finalDate: PropTypes.instanceOf(Date),
    label: PropTypes.node,
    fullWidth: PropTypes.bool,
    onChange: PropTypes.func,
    onError: PropTypes.func,
    disabled: PropTypes.bool,
    ref: PropTypes.any,
    inputRef: PropTypes.any,
    message: PropTypes.string,
    responsiveBreakpoint: PropTypes.number,
    mode: PropTypes.oneOf(['days', 'months', 'years']),
};

InputDateRange.defaultProps = {
    maxDaysInterval: 10,
    initialDate: null,
    finalDate: null,
    label: '',
    fullWidth: false,
    onChange: () => null,
    onError: () => null,
    disabled: false,
    ref: null,
    inputRef: null,
    message: '',
    responsiveBreakpoint: 992,
    mode: 'days',
};
